/**
 * Configuration for OptanonConsent cookie generation
 */
export interface OptanonConsentConfig {
  groups?: Record<string, 0 | 1>;
  isGpcEnabled?: 0 | 1;
  version?: string;
  browserGpcFlag?: 0 | 1;
  geolocation?: string;
  isIABGlobal?: boolean;
  hosts?: string;
  interactionCount?: number;
  isAnonUser?: 0 | 1;
  landingPath?: string;
}

/**
 * Default OptanonConsent configuration
 */
export const DEFAULT_OPTANON_CONFIG: OptanonConsentConfig = {
  // For now we will keep this empty and not being used.
};

/**
 * Formats a timestamp for OptanonConsent cookie
 * @param timestamp - Unix timestamp in milliseconds (optional, defaults to now)
 * @returns Formatted timestamp string
 */
export function formatOptanonTimestamp(timestamp?: number): string {
  const date = new Date(timestamp || Date.now());

  // Format: "Tue Sep 24 2025 14:30:45 GMT-0300 (Brasília Standard Time)"
  return date.toString();
}

/**
 * Converts groups object to OptanonConsent groups string format
 * @param groups - Groups configuration object
 * @returns Formatted groups string (e.g., "1:1,2:1,3:0,4:0")
 */
export function formatConsentGroups(groups: Record<string, 0 | 1>): string {
  return Object.entries(groups)
    .map(([groupId, status]) => `${groupId}:${status}`)
    .join(',');
}

/**
 * Generates a complete OptanonConsent cookie string
 * @param config - Configuration for the cookie (optional, uses defaults)
 * @returns Complete OptanonConsent cookie value
 */
export function generateOptanonConsentString(
  config: Partial<OptanonConsentConfig> = {}
): string {
  const finalConfig = { ...DEFAULT_OPTANON_CONFIG, ...config };

  const timestamp = formatOptanonTimestamp();
  const groupsString = formatConsentGroups(finalConfig.groups);

  // Build cookie string with all parameters
  const cookieParts = [
    '=undefined', // Initial value always undefined
    `groups=${groupsString}`,
    `datestamp=${encodeURIComponent(timestamp)}`
  ];

  return cookieParts.join('&');
}
